/**
 * Focus helpers for walkthrough-driven menus.
 *
 * A step may open a menu whose Spark search field autofocuses itself. When
 * the step becomes active while the user is still typing elsewhere (the
 * Labels tag input, say) that would steal their caret, so the hook waits
 * until focus reaches the menu's anchor on its own (Tab or click).
 */

const NON_TEXT_INPUT_TYPES = new Set([
    'button',
    'checkbox',
    'color',
    'file',
    'hidden',
    'image',
    'radio',
    'range',
    'reset',
    'submit',
]);

/** True for an element the user types into: text-like inputs, textareas, contenteditable, role=textbox. */
export function isTextEntryElement(element) {
    if (!element || element === document.body || typeof element.closest !== 'function') return false;
    const tag = element.tagName;
    if (tag === 'TEXTAREA') return true;
    if (tag === 'INPUT') return !NON_TEXT_INPUT_TYPES.has(String(element.type || 'text').toLowerCase());
    if (element.isContentEditable) return true;
    const editable = element.getAttribute?.('contenteditable');
    if (editable !== null && editable !== undefined && editable !== 'false') return true;
    return element.getAttribute?.('role') === 'textbox';
}

/**
 * True when opening a menu anchored at `anchorSelector` right now would
 * steal the caret: a text-entry element outside the anchor owns focus.
 */
export function shouldDeferUntilFocus(anchorSelector) {
    if (!anchorSelector || typeof document === 'undefined') return false;
    const focused = document.activeElement;
    if (!isTextEntryElement(focused)) return false;
    return !focused.closest(anchorSelector);
}

/**
 * Calls `onFocus` once keyboard focus lands inside `anchorSelector`.
 *
 * Focus that arrives through a pointer press is ignored: the trigger's own
 * click toggles the menu right after `focusin`, so opening it here too
 * would close it again. Listeners sit on `document` (capture) so an anchor
 * that mounts later is still caught. Returns a function that stops waiting.
 */
export function deferUntilFocusWithin(anchorSelector, onFocus) {
    let pointerPending = false;
    const within = (target) => Boolean(target?.closest?.(anchorSelector));

    const stop = () => {
        document.removeEventListener('pointerdown', handlePointerDown, true);
        document.removeEventListener('pointerup', clearPointer, true);
        document.removeEventListener('pointercancel', clearPointer, true);
        document.removeEventListener('focusin', handleFocusIn, true);
    };
    function handlePointerDown(event) {
        pointerPending = within(event.target);
    }
    function clearPointer() {
        pointerPending = false;
    }
    function handleFocusIn(event) {
        if (!within(event.target)) return;
        const viaPointer = pointerPending;
        stop();
        if (!viaPointer) onFocus();
    }

    document.addEventListener('pointerdown', handlePointerDown, true);
    document.addEventListener('pointerup', clearPointer, true);
    document.addEventListener('pointercancel', clearPointer, true);
    document.addEventListener('focusin', handleFocusIn, true);
    return stop;
}
